import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Star } from 'lucide-react';

const DEFAULT_CENTER = [9.9281, -84.0907];

// Deterministic jitter so the circle never reveals the exact point (Airbnb-style)
const jitterPosition = (id, lat, lng) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const dx = (((Math.abs(h) % 1000) / 1000) - 0.5) * 0.008;
  const dy = (((Math.abs(h >> 10) % 1000) / 1000) - 0.5) * 0.008;
  return [lat + dx, lng + dy];
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
  }, [points, map]);
  return null;
};

const TalleresMap = ({ talleres, hoveredId, onViewTaller, height = '100%' }) => {
  const items = useMemo(
    () =>
      talleres
        .filter((t) => t.location?.lat && t.location?.lng)
        .map((t) => ({ ...t, pos: jitterPosition(t.id, t.location.lat, t.location.lng) })),
    [talleres]
  );

  const points = useMemo(() => items.map((t) => t.pos), [items]);

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 relative z-0" style={{ height }} data-testid="talleres-map">
      <MapContainer center={DEFAULT_CENTER} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {items.map((t) => {
          const active = hoveredId === t.id;
          const popupContent = (
            <Popup>
              <div className="text-sm min-w-[180px]">
                <p className="font-bold text-slate-900">{t.company_name}</p>
                <p className="flex items-center gap-1 text-slate-600 my-1">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span className="font-semibold">{t.rating}</span>
                  <span className="text-xs">({t.total_reviews} reseñas)</span>
                </p>
                <p className="text-xs text-slate-500 mb-2">{t.location?.city} · zona aproximada</p>
                <button
                  onClick={() => onViewTaller(t.id)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-md py-1.5 transition-colors"
                  data-testid={`map-popup-view-${t.id}`}
                >
                  Ver detalles
                </button>
              </div>
            </Popup>
          );
          return (
            <React.Fragment key={t.id}>
              {/* Área aproximada (visible al acercar) */}
              <Circle
                center={t.pos}
                radius={active ? 850 : 700}
                pathOptions={{
                  color: active ? '#7c3aed' : '#8b5cf6',
                  weight: active ? 3 : 1.5,
                  fillColor: '#8b5cf6',
                  fillOpacity: active ? 0.4 : 0.18,
                }}
              >
                {popupContent}
              </Circle>
              {/* Punto visible a cualquier zoom */}
              <CircleMarker
                center={t.pos}
                radius={active ? 9 : 6}
                pathOptions={{
                  color: '#ffffff',
                  weight: 2,
                  fillColor: active ? '#7c3aed' : '#8b5cf6',
                  fillOpacity: 1,
                }}
              >
                {popupContent}
              </CircleMarker>
            </React.Fragment>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default TalleresMap;
